// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2023 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import CheckboxPresentationPropsDefault from "@insite/mobius/Checkbox/presentationProps";
import TypographyStyle from "@insite/mobius/Typography/TypographyStyle";
import Success1 from "@werner/XDL/Icons/Success1";
import { xBaseTheme } from "@werner/XDL/types";
import color from "@werner/XDL/Utilities/color";
import { css } from "styled-components";

export const CheckboxInputSelect = `span[role="checkbox"]`;

export const CheckboxCheckedSelector = `
    &[aria-checked="true"]:not([aria-disabled="true"]),
    &[aria-checked="mixed"]:not([aria-disabled="true"])
`;

export const CheckboxDefaultSelector = `&[aria-checked="false"]:not([aria-disabled="true"])`;
export const CheckboxDisabledSelector = `&[aria-disabled="true"]`;

const defaultStateStyles = css`
    & ${CheckboxInputSelect} {
        height: 22px;
        min-width: 22px;
        width: 22px;
        border-color: ${color("common.ui1")};
        border-radius: 2px;
        background: ${color("common.white")};
        display: flex;
        justify-content: center;
        align-items: center;
        &:focus {
            box-shadow: none;
        }
        & + label {
            margin-left: 6px;
        }
    }
`;

const checkedStyle = css`
    & ${CheckboxInputSelect} {
        ${CheckboxCheckedSelector} {
            background: ${color("primary")};
            border-color: ${color("primary")};
            color: ${color("common.white")};
        }
    }
`;

const hoverStyle = css`
    // this media query will disable hover style for touch devices, allowing the proper style to take place
    @media (hover: hover) {
        &:hover {
            ${CheckboxInputSelect} {
                ${CheckboxCheckedSelector} {
                    background: ${color("common.ui3")};
                    border-color: ${color("common.ui3")};
                }
                ${CheckboxDefaultSelector} {
                    border: 2px solid ${color("common.ui3")};
                }
            }
        }
    }
`;

const focusStyle = css`
    &:focus {
        ${CheckboxInputSelect} {
            ${CheckboxDefaultSelector} {
                border: 2px solid ${color("primary")};
            }
        }
    }
`;

const disabledStyle = css`
    ${CheckboxInputSelect} {
        ${CheckboxDisabledSelector}, ${CheckboxDisabledSelector} + label {
            opacity: 0.5;
            cursor: not-allowed;
        }
    }
`;

export default {
    defaultProps: {
        ...CheckboxPresentationPropsDefault,
        mergeCss: true,
        typographyProps: {
            mergeCss: true,
            size: 15,
            weight: 400,
            color: "text.main",
            css: css`
                letter-spacing: 0;
                line-height: 18px;
                user-select: none;
            `,
        },
        iconProps: {
            size: 14,
            src: Success1,
            color: "common.white",
        },
        css: css`
            &:not(:disabled) {
                cursor: pointer;
            }
            ${defaultStateStyles}
            ${hoverStyle}
            ${focusStyle}
            ${checkedStyle}
            ${disabledStyle}
        `,
    },
    groupDefaultProps: {
        labelProps: {
            variant: "h6",
        },
        errorProps: {
            css: css`
                &:empty {
                    display: none;
                }
            `,
        },
        css: css`
            & > ${TypographyStyle as any} {
                font-size: 15px;
                font-weight: bold;
                margin-bottom: 20px;
                &:empty {
                    display: none;
                }
            }
        `,
    },
} as xBaseTheme["checkbox"];
